<script lang="ts">
/**
 * @component Footer
 *
 * Platform-adaptive footer following Apple HIG responsive patterns. Renders as a
 * 4-column grid on macOS-class viewports (md+), 2-column on iPadOS-class (sm),
 * and single-column stack on iOS-class (mobile). Uses Liquid Glass material
 * (my-glass) for translucent Nord-themed background.
 *
 * Organises content into four sections (Brand, Navigation, Connect, Legal).
 *
 * **Brand section** -- displays the user name, title, and a row of social media
 * icon links (GitHub, Instagram, X/Twitter, TikTok, Threads, Bluesky) using
 * icons from `@icons-pack/svelte-simple-icons`.
 *
 * **Navigation section** -- internal page links mirroring the sidebar's
 * navigation group (Home, About Me, Projects, CV).
 *
 * **Connect section** -- contact and linkhub pages, plus a direct email link
 * sourced from the shared `contactDetails` module.
 *
 * **Legal section** -- imprint link and a cookie-settings button that dispatches
 * a `show-cookie-consent` custom DOM event to re-open the {@link CookieConsent}
 * banner.
 *
 * The footer is hidden in print layouts via `print:hidden` / `no-print` classes
 * and uses the `my-glass` utility for the frosted-glass card backdrop styled
 * with the Nord color palette.
 *
 * @see {@link AppSidebar} -- Primary navigation sidebar with the same link groups
 * @see {@link CommandMenu} -- Command Palette toggled by the footer button
 * @see {@link LocalizedLink} -- Locale-aware anchor used for all internal links
 */

import { onMount } from "svelte";
import { Mail, Cookie } from "@lucide/svelte";
import LocalizedLink from "$lib/components/LocalizedLink.svelte";
import CommandMenuButton from "$lib/components/command-menu/CommandMenuButton.svelte";
import CookieConsent from "$lib/components/cookie/CookieConsent.svelte";
import { contactDetails } from "$lib/contact";
import {
  SiGithub as Github,
  SiGitlab as Gitlab,
  SiInstagram as Instagram,
  SiX as Twitter,
  SiTiktok as TikTok,
  SiThreads as Threads,
  SiBluesky as Bluesky,
} from "@icons-pack/svelte-simple-icons";
import { cn } from "$lib/utils";
import { t } from "$lib/i18n";
import { getAnalytics } from "$lib/remote/analytics.remote";
import { IsMobile } from "$lib/hooks/is-mobile.svelte";
import { IsTablet } from "$lib/hooks/is-tablet.svelte";
import Langswitcher from "./Langswitcher.svelte";

const currentYear = new Date().getFullYear();

/** Derived array of primary internal navigation links. Re-computes when locale changes. */
let navigationLinks = $derived([
  { href: "/", label: $t("sites.root") },
  { href: "/aboutme", label: $t("sites.aboutme") },
  { href: "/projects", label: $t("sites.projects") },
  { href: "/cv", label: $t("sites.cv") },
]);

/** Derived array of connect links (Contact, Linkhub). Re-computes when locale changes. */
let connectLinks = $derived([
  { href: "/contact", label: $t("sites.contact") },
  { href: "/linkhub", label: $t("sites.linkhub") },
]);

/** Static map to resolve Simple Icons by social media name. */
const iconMap: Record<string, any> = {
  GitLab: Gitlab,
  GitHub: Github,
  Instagram: Instagram,
  "X/Twitter": Twitter,
  TikTok: TikTok,
  Threads: Threads,
  Bluesky: Bluesky,
};

/** Derived array of external social media links built from central contactDetails. */
const socialLinks = contactDetails.socialMedia.map((s) => ({
  href: s.link,
  label: s.name,
  icon: iconMap[s.name],
}));

/** Derived array of legal links (Imprint). Re-computes when locale changes. */
let legalLinks = $derived([{ href: "/imprint", label: $t("sites.imprint") }]);

/**
 * Dispatches a `show-cookie-consent` custom event on the window to re-open
 * the {@link CookieConsent} dialog, allowing the user to update their cookie
 * preferences from the footer.
 */
function showCookieSettings() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("show-cookie-consent"));
  }
}

const isMobile = new IsMobile();
const isTablet = new IsTablet();

let analyticsData = $state<{ active: number; total: number } | null>(null);

onMount(() => {
  let active = true;
  getAnalytics({})
    .then((data) => {
      if (active && data) {
        analyticsData = data;
      }
    })
    .catch(() => {});
  return () => {
    active = false;
  };
});
</script>

<footer
  id="footer"
  class={cn(
    "w-full z-10 print:hidden mt-auto p-2 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-2",
  )}
>
  <div class={cn("my-glass rounded-sm")}>
    <div class={cn("mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 lg:py-12")}>
      <!-- Main Footer Content -->
      <div
        class={cn(
          "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12",
        )}
      >
        <!-- Brand Section -->
        <div class={cn("sm:col-span-2 md:col-span-1")}>
          <LocalizedLink
            href="/"
            class={cn(
              "text-lg font-semibold text-foreground hover:text-primary transition-colors",
            )}
          >
            {$t("aboutme.name")}
          </LocalizedLink>
          <p
            class={cn(
              "mt-3 text-sm text-muted-foreground leading-relaxed hyphens-auto",
            )}
          >
            {$t("aboutme.title")}
          </p>
          <!-- Social Icons -->
          <div class={cn("mt-4 flex flex-wrap items-center gap-3")}>
            {#each socialLinks as social}
              <LocalizedLink
                href={social.href}
                target="_blank"
                rel="noreferrer"
                class={cn(
                  "text-muted-foreground hover:text-foreground transition-colors p-2 hover:bg-muted/50 rounded-md",
                )}
                aria-label={social.label}
              >
                <!-- svelte-ignore svelte_component_deprecated -->
                <svelte:component this={social.icon} title={social.label} />
              </LocalizedLink>
            {/each}
          </div>
        </div>

        <!-- Navigation -->
        <div>
          <h3 class={cn("text-sm font-semibold text-foreground mb-4")}>
            {$t("navigation")}
          </h3>
          <ul class={cn("space-y-3")}>
            {#each navigationLinks as link}
              <li>
                <LocalizedLink
                  href={link.href}
                  class={cn(
                    "text-sm text-muted-foreground hover:text-foreground transition-colors",
                  )}
                >
                  {link.label}
                </LocalizedLink>
              </li>
            {/each}
          </ul>
        </div>

        <!-- Connect -->
        <div>
          <h3 class={cn("text-sm font-semibold text-foreground mb-4")}>
            {$t("footer.connect")}
          </h3>
          <ul class={cn("space-y-3")}>
            {#each connectLinks as link}
              <li>
                <LocalizedLink
                  href={link.href}
                  class={cn(
                    "text-sm text-muted-foreground hover:text-foreground transition-colors",
                  )}
                >
                  {link.label}
                </LocalizedLink>
              </li>
            {/each}
            <li>
              <LocalizedLink
                href={contactDetails.email.link}
                class={cn(
                  "text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5",
                )}
              >
                <Mail class={cn("h-3.5 w-3.5")} />
                {$t("cv.about.email")}
              </LocalizedLink>
            </li>
          </ul>
        </div>

        <!-- Legal -->
        <div>
          <h3 class={cn("text-sm font-semibold text-foreground mb-4")}>
            {$t("footer.legal")}
          </h3>
          <ul class={cn("space-y-3")}>
            {#each legalLinks as link}
              <li>
                <LocalizedLink
                  href={link.href}
                  class={cn(
                    "text-sm text-muted-foreground hover:text-foreground transition-colors",
                  )}
                >
                  {link.label}
                </LocalizedLink>
              </li>
            {/each}
            <li>
              <button
                type="button"
                onclick={showCookieSettings}
                class={cn(
                  "text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5",
                )}
              >
                <Cookie class={cn("h-3.5 w-3.5")} />
                {$t("cookieConsent.cookieSettings")}
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Divider -->
      <div class={cn("mt-8 pt-8 border-t border-border/50")}></div>

      <!-- Bottom Bar -->
      <div class={cn("bg-muted/50 rounded-sm border-t border-border/50")}>
        <div
          class={cn(
            "flex flex-col sm:flex-row items-center justify-between gap-4 p-4",
          )}
        >
          <!-- Copyright -->
          <p class={cn("text-sm text-muted-foreground")}>
            &copy; {currentYear}
            {$t("aboutme.name")}. {$t("footer.rights")}
          </p>

          <!-- Analytics Counter -->
          {#if analyticsData}
            <div
              class="flex items-center gap-4 text-xs text-muted-foreground bg-background/50 px-3 py-1.5 rounded-full border border-border/50"
            >
              <span
                class="flex items-center gap-2"
                title="Active users (last 30 mins)"
              >
                <span class="relative flex h-2 w-2">
                  <span
                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
                  ></span>
                  <span
                    class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"
                  ></span>
                </span>
                {analyticsData.active}
              </span>
              <span class="opacity-50">|</span>
              <span class="flex items-center gap-1.5" title="Total visitors">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-users"
                  ><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle
                    cx="9"
                    cy="7"
                    r="4"
                  /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path
                    d="M16 3.13a4 4 0 0 1 0 7.75"
                  /></svg
                >
                {analyticsData.total.toLocaleString()}
              </span>
            </div>
          {/if}

          <!-- Command Palette Button (Desktop) / Langswitcher (Mobile) -->
          <div class={cn("hidden md:block")}>
            <CommandMenuButton />
          </div>
          <div class={cn("block md:hidden")}>
            <Langswitcher />
          </div>

          <LocalizedLink
            href="/imprint#ai-label"
            class={cn("flex flex-row items-center gap-2")}
          >
            <img
              src="/pictures/ai-label/eu-official/LABEL_AI_white transparent.svg"
              alt={$t("aiLabel.title")}
              class="h-6 w-6"
            />
          </LocalizedLink>
        </div>
      </div>
    </div>
  </div>
</footer>
